<template>
    <div ref="templateRoot" class="pdf-container" style="background-color: #ffffff !important; color: #000000 !important; isolation: isolate;">
        
        <!-- NAGŁÓWEK GŁÓWNY (Miejsce, data + Logo) -->
        <div class="d-flex justify-content-between align-items-start mb-3">
            <div class="header-dates">
                <div class="date-row">
                    <span class="date-label">Miejsce wystawienia:</span>
                    <span class="date-dots"></span>
                    <span class="date-value">{{ contract.issuePlace || '---' }}</span>
                </div>
                <div class="date-row">
                    <span class="date-label">Data wystawienia:</span>
                    <span class="date-dots"></span>
                    <span class="date-value">{{ contract.issueDate || '---' }}</span>
                </div>
            </div>

            <!-- Logo po prawej -->
            <div class="header-logo text-end">
                <img v-if="contract.sellerLogoUrl" :src="contract.sellerLogoUrl" alt="Logo" style="max-height: 55px; max-width: 220px; object-fit: contain;" />
                <h3 v-else class="fw-bold text-danger m-0 text-lowercase fs-4">{{ contract.seller?.companyName || 'autokutno' }}</h3>
            </div>
        </div>

        <!-- TYTUŁ DOKUMENTU -->
        <div class="text-center my-3">
            <h4 class="fw-normal fs-3 m-0">Umowa komisowa nr {{ contract.docNumber || '---' }}</h4>
        </div>

        <!-- BLOK: DANE KOMISU + POJAZD -->
        <div class="row g-2 mb-3 align-items-stretch">
            <!-- Dane komisu -->
            <div class="col-6">
                <div class="card h-100 border rounded-0">
                    <div class="card-header bg-gray py-1 px-2 fw-normal fs-7 border-bottom">Dane komisu:</div>
                    <div class="card-body p-2 fs-7 line-height-sm">
                        <div class="fw-bold">Nazwa firmy: {{ contract.seller?.companyName || formatName(contract.seller) }}</div>
                        <div v-if="contract.seller?.nip">NIP: {{ contract.seller.nip }}<span v-if="contract.seller?.regon">, REGON: {{ contract.seller.regon }}</span></div>
                        <div v-if="contract.seller?.country">Kraj: {{ contract.seller.country }}</div>
                        <div v-if="contract.seller?.city">Miasto: {{ contract.seller.postalCode }} {{ contract.seller.city }}</div>
                        <div v-if="contract.seller?.street">Ulica: {{ contract.seller.street }}</div>
                        <div v-if="contract.seller?.phone">Telefon: {{ contract.seller.phone }}</div>
                        <div v-if="contract.seller?.email">E-mail: {{ contract.seller.email }}</div>
                    </div>
                </div>
            </div>

            <!-- Pojazd -->
            <div class="col-6">
                <div class="card h-100 border rounded-0">
                    <div class="card-header bg-gray py-1 px-2 fw-normal fs-7 border-bottom">Pojazd:</div>
                    <div class="card-body p-2 fs-7 line-height-sm">
                        <div><strong>Marka / Model:</strong> {{ contract.car?.make }} {{ contract.car?.model }}</div>
                        <div><strong>Rok produkcji:</strong> {{ contract.car?.productionYear || '---' }}</div>
                        <div><strong>Nr VIN:</strong> {{ contract.car?.vin || '---' }}</div>
                        <div><strong>Nr rejestracyjny:</strong> {{ contract.car?.registrationPlate || '---' }}</div>
                        <div><strong>Pojemność / Moc:</strong> {{ contract.car?.engineCapacity || '---' }} cm³ / {{ contract.car?.powerHp || '---' }} KM</div>
                        <div><strong>Przebieg:</strong> {{ contract.car?.mileage || '---' }} km</div>
                        <div><strong>Kolor:</strong> {{ contract.car?.color || '---' }}</div>
                    </div>
                </div>
            </div>
        </div>

        <!-- KOMITENT -->
        <div class="card mb-3 border rounded-0">
            <div class="card-header bg-gray py-1 px-2 fw-normal fs-7 border-bottom">Komitent:</div>
            <div class="card-body p-2 fs-7 line-height-sm">
                <div class="fw-bold">{{ formatName(contract.buyer || contract.komitent) }}</div>
                <div v-if="contract.buyer?.pesel || contract.komitent?.pesel">PESEL: {{ contract.buyer?.pesel || contract.komitent?.pesel }}</div>
                <div v-if="contract.buyer?.nip || contract.komitent?.nip">NIP: {{ contract.buyer?.nip || contract.komitent?.nip }}</div>
                <div v-if="contract.buyer?.street || contract.komitent?.street">Adres: {{ contract.buyer?.street || contract.komitent?.street }}, {{ contract.buyer?.postalCode || contract.komitent?.postalCode }} {{ contract.buyer?.city || contract.komitent?.city }}</div>
                <div v-if="contract.buyer?.phone || contract.komitent?.phone">Tel: {{ contract.buyer?.phone || contract.komitent?.phone }}</div>
            </div>
        </div>

        <!-- PARAGRAFY UMOWY (DYNAMICZNIE RENDEROWANE Z PUNKTÓW) -->
        <div class="provisions-content small line-height-md">
            <div v-for="(point, idx) in formattedProvisions" :key="idx" class="mb-2">
                <strong>§{{ idx + 1 }}</strong><br>
                <span>{{ point }}</span>
            </div>
        </div>

        <!-- Podpisy -->
        <div class="row pt-5 mt-2 text-center small">
            <div class="col-6">
                <div class="border-top border-dark pt-1 mx-4">Komitent</div>
            </div>
            <div class="col-6">
                <div class="border-top border-dark pt-1 mx-4">Komisant / Pośrednik</div>
            </div>
        </div>

    </div>
</template>

<script setup>
import { ref, computed } from 'vue'

const props = defineProps({
    contract: { type: Object, required: true }
})

const templateRoot = ref(null)

defineExpose({
    element: templateRoot
})

const formatName = (party) => {
    if (!party) return ''
    if (party.companyName) return party.companyName
    return `${party.firstName || ''} ${party.lastName || ''}`.trim()
}

const formatMoney = (val) => {
    if (!val && val !== 0) return '0.00'
    return Number(val).toLocaleString('pl-PL', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
}

const formattedProvisions = computed(() => {
    let list = props.contract.provisions || []
    if (typeof list === 'string') {
        list = list.split('\n').filter(Boolean)
    }

    const carPriceFormatted = `${formatMoney(props.contract.carPrice)} ${props.contract.carCurrency || 'PLN'}`
    const commissionFormatted = `${formatMoney(props.contract.commissionAmount || props.contract.komisantAmount)} ${props.contract.commissionCurrency || 'PLN'}`

    return list.map(point => {
        return point
            .replace('{cenapojazdu}', carPriceFormatted)
            .replace('{prowizjakomisanta}', commissionFormatted)
    })
})
</script>

<style scoped>
.pdf-container,
.pdf-container * {
    color: #000000 !important;
    box-sizing: border-box;
}

.pdf-container {
    width: 210mm;
    padding: 10mm 15mm;
    font-family: Arial, sans-serif;
    font-size: 10px;
    background: #ffffff !important;
    color: #000000 !important;
    line-height: 1.3;
    isolation: isolate;
}

.header-dates {
    width: 260px;
}
.date-row {
    display: flex;
    align-items: baseline;
    font-size: 10.5px;
    margin-bottom: 2px;
}
.date-label {
    white-space: nowrap;
}
.date-dots {
    flex-grow: 1;
    border-bottom: 1px dotted #888888;
    margin: 0 4px;
}
.date-value {
    white-space: nowrap;
    font-weight: 500;
}

.bg-gray {
    background-color: #e2e2e2 !important;
}

.fs-7 {
    font-size: 10px;
}

.line-height-sm {
    line-height: 1.25;
}

.line-height-md {
    line-height: 1.35;
}

.pdf-container .card {
    background-color: #ffffff !important;
    border-color: #cccccc !important;
}
</style>